/**
 * @fileoverview Kanban View Component for visual task and order management.
 * Provides a drag-and-drop board interface inspired by Frappe CRM for managing
 * items across different status columns. Supports customizable columns, card
 * rendering, and real-time status updates.
 * @module components/ui/KanbanView
 */

import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Chip,
    IconButton,
    Button,
    Menu,
    MenuItem,
    Skeleton,
    Tooltip,
    Badge,
    Avatar,
    Divider
} from '@mui/material';
import {
    MoreVert as MoreIcon,
    Add as AddIcon,
    DragIndicator as DragIcon,
    Visibility as ViewIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    ExpandMore as ExpandIcon,
    Schedule as ScheduleIcon,
    Person as PersonIcon,
    AttachMoney as MoneyIcon
} from '@mui/icons-material';
import { getCurrencySymbol } from '../../utils/invoice-pdf-general-section-utilities';

/**
 * Color mapping for different column/status types.
 * @constant {Object.<string, string>}
 */
const COLUMN_COLORS = {
    draft: '#9e9e9e',
    pending: '#2196f3',
    processing: '#ff9800',
    ready_to_ship: '#9c27b0',
    shipped: '#00bcd4',
    delivered: '#4caf50',
    cancelled: '#f44336',
    returned: '#ff5722',
    on_hold: '#607d8b',
    new: '#2196f3',
    in_progress: '#ff9800',
    completed: '#4caf50',
    blocked: '#f44336'
};

/**
 * Individual card component for Kanban board items.
 * Displays item details with drag-and-drop support and action menu.
 * @param {Object} props - Component props
 * @param {Object} props.item - The item data to display
 * @param {string} props.item.id - Unique identifier for the item
 * @param {string} [props.item.title] - Item title
 * @param {string} [props.item.description] - Item description
 * @param {string} [props.item.priority] - Priority level (high, medium, low)
 * @param {string[]} [props.item.tags] - Array of tag labels
 * @param {string|Object} [props.item.assignee] - Assigned user name or object
 * @param {string} [props.item.dueDate] - Due date ISO string
 * @param {number} [props.item.amount] - Monetary amount
 * @param {Function} [props.onView] - Callback when view action is clicked
 * @param {Function} [props.onEdit] - Callback when edit action is clicked
 * @param {Function} [props.onDelete] - Callback when delete action is clicked
 * @param {Function} props.onDragStart - Callback when drag starts
 * @param {Function} props.onDragEnd - Callback when drag ends
 * @param {Function} [props.renderCard] - Custom card renderer function
 * @param {boolean} props.isDragging - Whether the card is currently being dragged
 * @returns {JSX.Element} Rendered Kanban card
 */
const KanbanCard = ({
    item, 
    onView, 
    onEdit, 
    onDelete, 
    onDragStart,
    onDragEnd,
    renderCard,
    isDragging 
}) => {
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState(null);

    const handleMenuOpen = (event) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleAction = (action) => {
        handleMenuClose();
        action(item);
    };

    const defaultCardContent = (
        <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>
                    {item.title || item.name || item.orderNumber || `#${item.id}`}
                </Typography>
                <IconButton size="small" onClick={handleMenuOpen} sx={{ ml: 1, mt: -0.5 }}>
                    <MoreIcon fontSize="small" />
                </IconButton>
            </Box>
            
            {item.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.4 }}>
                    {item.description.length > 80 ? `${item.description.substring(0, 80)}...` : item.description}
                </Typography>
            )}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                {item.priority && (
                    <Chip 
                        label={item.priority} 
                        size="small" 
                        color={item.priority === 'high' ? 'error' : item.priority === 'medium' ? 'warning' : 'default'}
                        sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                )}
                {item.tags?.map((tag, idx) => (
                    <Chip key={idx} label={tag} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                ))}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 'auto' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {item.assignee && (
                        <Tooltip title={item.assignee.name || item.assignee}>
                            <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                                {(item.assignee.name || item.assignee).charAt(0).toUpperCase()}
                            </Avatar>
                        </Tooltip>
                    )}
                    {item.dueDate && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <ScheduleIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                                {new Date(item.dueDate).toLocaleDateString()}
                            </Typography>
                        </Box>
                    )}
                </Box>
                {item.amount !== undefined && (
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'success.main' }}>
                        {typeof item.amount === 'number' 
                            ? `${getCurrencySymbol(item.currency || 'EUR')}${item.amount.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                            : item.amount
                        }
                    </Typography>
                )}
            </Box>
        </>
    );

    return (
        <Card
            draggable
            onDragStart={(e) => onDragStart(e, item)}
            onDragEnd={onDragEnd}
            sx={{
                mb: 1.5,
                cursor: 'grab',
                transition: 'all 0.2s ease',
                opacity: isDragging ? 0.5 : 1,
                transform: isDragging ? 'rotate(3deg)' : 'none',
                '&:hover': {
                    boxShadow: 3,
                    transform: 'translateY(-2px)'
                },
                '&:active': {
                    cursor: 'grabbing'
                }
            }}
        >
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                {renderCard ? renderCard(item) : defaultCardContent}
            </CardContent>

            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                {onView && (
                    <MenuItem onClick={() => handleAction(onView)}>
                        <ViewIcon fontSize="small" sx={{ mr: 1 }} />
                        {t('common.view', 'View')}
                    </MenuItem>
                )}
                {onEdit && (
                    <MenuItem onClick={() => handleAction(onEdit)}>
                        <EditIcon fontSize="small" sx={{ mr: 1 }} />
                        {t('common.edit', 'Edit')}
                    </MenuItem>
                )}
                {onDelete && (
                    <>
                        <Divider />
                        <MenuItem onClick={() => handleAction(onDelete)} sx={{ color: 'error.main' }}>
                            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                            {t('common.delete', 'Delete')}
                        </MenuItem>
                    </>
                )}
            </Menu>
        </Card>
    );
};

/**
 * Column component for Kanban board displaying items in a specific status.
 * Handles drag-and-drop target functionality and item rendering.
 * @param {Object} props - Component props
 * @param {Object} props.column - Column configuration
 * @param {string} props.column.id - Unique column identifier
 * @param {string} [props.column.label] - Display label for the column
 * @param {string} [props.column.color] - Column header color
 * @param {number} [props.column.count] - Override item count display
 * @param {Object[]} props.items - Array of items to display in this column
 * @param {Function} props.onDragStart - Callback when item drag starts
 * @param {Function} props.onDragEnd - Callback when item drag ends
 * @param {Function} props.onDragOver - Callback when item is dragged over column
 * @param {Function} props.onDrop - Callback when item is dropped on column
 * @param {Function} [props.onView] - Callback when view action is clicked
 * @param {Function} [props.onEdit] - Callback when edit action is clicked
 * @param {Function} [props.onDelete] - Callback when delete action is clicked
 * @param {Function} [props.onAddNew] - Callback when add new button is clicked
 * @param {Function} [props.onLoadMore] - Callback when load more is clicked
 * @param {boolean} [props.hasMore] - Whether more items can be loaded
 * @param {boolean} [props.isLoading] - Whether column is in loading state
 * @param {Function} [props.renderCard] - Custom card renderer function
 * @param {Object} [props.draggingItem] - Currently dragged item for visual feedback
 * @returns {JSX.Element} Rendered Kanban column
 */
const KanbanColumn = ({
    column,
    items,
    onDragStart,
    onDragEnd,
    onDragOver,
    onDrop,
    onView,
    onEdit,
    onDelete,
    onAddNew,
    onLoadMore,
    hasMore,
    isLoading,
    renderCard,
    draggingItem
}) => {
    const { t } = useTranslation();
    const columnColor = COLUMN_COLORS[column.id?.toLowerCase()] || column.color || '#667eea';
    const itemCount = column.count !== undefined ? column.count : items.length;

    return (
        <Box
            onDragOver={onDragOver}
            onDrop={(e) => onDrop(e, column.id)}
            sx={{
                minWidth: 280,
                maxWidth: 320,
                flex: '0 0 300px',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'grey.50',
                borderRadius: 2,
                overflow: 'hidden',
                height: 'fit-content',
                maxHeight: 'calc(100vh - 200px)'
            }}
        >
            <Box
                sx={{
                    p: 1.5,
                    borderBottom: `3px solid ${columnColor}`,
                    bgcolor: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {column.label || column.name || column.id}
                    </Typography>
                    <Badge 
                        badgeContent={itemCount} 
                        color="primary"
                        sx={{ '& .MuiBadge-badge': { bgcolor: columnColor } }}
                    />
                </Box>
                {onAddNew && (
                    <Tooltip title={t('common.addNew', 'Add New')}>
                        <IconButton size="small" onClick={() => onAddNew(column.id)}>
                            <AddIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            <Box
                sx={{
                    p: 1,
                    flex: 1,
                    overflowY: 'auto',
                    minHeight: 100,
                    '&::-webkit-scrollbar': { width: 6 },
                    '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.300', borderRadius: 3 }
                }}
            >
                {isLoading ? (
                    [...Array(3)].map((_, i) => (
                        <Skeleton key={i} variant="rectangular" height={100} sx={{ mb: 1.5, borderRadius: 1 }} />
                    ))
                ) : items.length > 0 ? (
                    items.map((item) => (
                        <KanbanCard
                            key={item.id}
                            item={item}
                            onView={onView}
                            onEdit={onEdit}
                            onDelete={onDelete}
                            onDragStart={onDragStart}
                            onDragEnd={onDragEnd}
                            renderCard={renderCard}
                            isDragging={draggingItem?.id === item.id}
                        />
                    ))
                ) : (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            {t('kanban.noItems', 'No items')}
                        </Typography>
                    </Box>
                )}

                {hasMore && !isLoading && (
                    <Button
                        fullWidth
                        size="small"
                        onClick={() => onLoadMore(column.id)}
                        startIcon={<ExpandIcon />}
                        sx={{ mt: 1 }}
                    >
                        {t('common.loadMore', 'Load More')}
                    </Button>
                )}
            </Box>
        </Box>
    );
};

/**
 * Main Kanban board component for visual task and order management.
 * Provides drag-and-drop functionality for moving items between status columns.
 * Inspired by Frappe CRM's Kanban view for intuitive workflow management.
 * @param {Object} props - Component props
 * @param {Object[]} props.columns - Array of column configurations
 * @param {string} props.columns[].id - Unique column identifier (matches item status)
 * @param {string} [props.columns[].label] - Display label for the column
 * @param {string} [props.columns[].color] - Column header color
 * @param {Object[]} props.items - Array of items to display across columns
 * @param {string} [props.groupBy='status'] - Property name to group items by
 * @param {Function} [props.onStatusChange] - Callback when item status changes via drag-drop
 * @param {Function} [props.onView] - Callback when view action is clicked on item
 * @param {Function} [props.onEdit] - Callback when edit action is clicked on item
 * @param {Function} [props.onDelete] - Callback when delete action is clicked on item
 * @param {Function} [props.onAddNew] - Callback when add new button is clicked in column
 * @param {Function} [props.onLoadMore] - Callback when load more is clicked in column
 * @param {boolean} [props.isLoading=false] - Whether board is in loading state
 * @param {Function} [props.renderCard] - Custom card renderer function
 * @param {string} [props.emptyMessage] - Message to display when no columns configured
 * @returns {JSX.Element} Rendered Kanban board
 * @example
 * <KanbanView
 *   columns={ORDER_KANBAN_COLUMNS}
 *   items={orders}
 *   groupBy="status"
 *   onStatusChange={(item, newStatus) => updateOrder(item.id, { status: newStatus })}
 * />
 */
const KanbanView = ({
    columns,
    items,
    groupBy = 'status',
    onStatusChange,
    onView,
    onEdit,
    onDelete,
    onAddNew,
    onLoadMore,
    isLoading = false,
    renderCard,
    emptyMessage
}) => {
    const { t } = useTranslation();
    const [draggingItem, setDraggingItem] = useState(null);

    const groupedItems = useMemo(() => {
        const grouped = {};
        // Guard against undefined or non-array columns
        const safeColumns = Array.isArray(columns) ? columns : [];
        safeColumns.forEach(col => {
            grouped[col.id] = [];
        });
        
        // Guard against undefined or non-array items
        const safeItems = Array.isArray(items) ? items : [];
        safeItems.forEach(item => {
            const groupValue = item[groupBy]?.toLowerCase?.() || item[groupBy] || 'unknown';
            if (grouped[groupValue]) {
                grouped[groupValue].push(item);
            } else {
                const matchingColumn = columns.find(col => 
                    col.id?.toLowerCase() === groupValue?.toLowerCase() ||
                    col.values?.includes(groupValue)
                );
                if (matchingColumn) {
                    grouped[matchingColumn.id].push(item);
                }
            }
        });
        
        return grouped;
    }, [items, columns, groupBy]);

    const handleDragStart = useCallback((e, item) => {
        setDraggingItem(item);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', JSON.stringify(item));
    }, []);

    const handleDragEnd = useCallback(() => {
        setDraggingItem(null);
    }, []);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    }, []);

    const handleDrop = useCallback((e, targetColumnId) => {
        e.preventDefault();
        
        if (!draggingItem) return;
        
        const currentStatus = draggingItem[groupBy]?.toLowerCase?.() || draggingItem[groupBy];
        if (currentStatus === targetColumnId) return;

        if (onStatusChange) {
            onStatusChange(draggingItem, targetColumnId, currentStatus);
        }
        
        setDraggingItem(null);
    }, [draggingItem, groupBy, onStatusChange]);

    if (!columns || columns.length === 0) {
        return (
            <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                    {emptyMessage || t('kanban.noColumns', 'No columns configured')}
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                gap: 2,
                overflowX: 'auto',
                pb: 2,
                minHeight: 400,
                '&::-webkit-scrollbar': { height: 8 },
                '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.300', borderRadius: 4 }
            }}
        >
            {columns.map((column) => (
                <KanbanColumn
                    key={column.id}
                    column={column}
                    items={groupedItems[column.id] || []}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onView={onView}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onAddNew={onAddNew}
                    onLoadMore={onLoadMore}
                    hasMore={column.hasMore}
                    isLoading={isLoading}
                    renderCard={renderCard}
                    draggingItem={draggingItem}
                />
            ))}
        </Box>
    );
};

/**
 * Pre-configured column definitions for order management Kanban boards.
 * Covers the standard order lifecycle from draft to delivery.
 * @constant {Object[]}
 */
export const ORDER_KANBAN_COLUMNS = [
    { id: 'draft', label: 'Draft', color: COLUMN_COLORS.draft },
    { id: 'pending', label: 'Pending', color: COLUMN_COLORS.pending },
    { id: 'processing', label: 'Processing', color: COLUMN_COLORS.processing },
    { id: 'ready_to_ship', label: 'Ready to Ship', color: COLUMN_COLORS.ready_to_ship },
    { id: 'shipped', label: 'Shipped', color: COLUMN_COLORS.shipped },
    { id: 'delivered', label: 'Delivered', color: COLUMN_COLORS.delivered }
];

/**
 * Pre-configured column definitions for task management Kanban boards.
 * Covers standard task workflow states.
 * @constant {Object[]}
 */
export const TASK_KANBAN_COLUMNS = [
    { id: 'new', label: 'New', color: COLUMN_COLORS.new },
    { id: 'in_progress', label: 'In Progress', color: COLUMN_COLORS.in_progress },
    { id: 'blocked', label: 'Blocked', color: COLUMN_COLORS.blocked },
    { id: 'completed', label: 'Completed', color: COLUMN_COLORS.completed }
];

/**
 * Pre-configured column definitions for returns management Kanban boards.
 * Covers the return processing lifecycle.
 * @constant {Object[]}
 */
export const RETURN_KANBAN_COLUMNS = [
    { id: 'pending', label: 'Pending', color: COLUMN_COLORS.pending },
    { id: 'processing', label: 'Processing', color: COLUMN_COLORS.processing },
    { id: 'completed', label: 'Completed', color: COLUMN_COLORS.completed },
    { id: 'cancelled', label: 'Cancelled', color: COLUMN_COLORS.cancelled }
];

/**
 * Pre-configured column definitions for picking wave management Kanban boards.
 * Covers the wave lifecycle from open to closed/cancelled.
 * @constant {Object[]}
 */
export const WAVE_KANBAN_COLUMNS = [
    { id: 'open', label: 'Open', color: '#2196f3' },
    { id: 'released', label: 'Released', color: '#9c27b0' },
    { id: 'in_progress', label: 'In Progress', color: '#ff9800' },
    { id: 'closed', label: 'Closed', color: '#4caf50' },
    { id: 'cancelled', label: 'Cancelled', color: '#f44336' }
];

/**
 * Pre-configured column definitions for backorder queue Kanban boards.
 * Groups backorders by priority level for visual prioritization.
 * @constant {Object[]}
 */
export const BACKORDER_KANBAN_COLUMNS = [
    { id: 'high', label: 'High Priority', color: '#f44336' },
    { id: 'medium', label: 'Medium Priority', color: '#ff9800' },
    { id: 'low', label: 'Low Priority', color: '#4caf50' }
];

/**
 * Pre-configured column definitions for shipment management Kanban boards.
 * Covers the shipment lifecycle from pending to delivered.
 * @constant {Object[]}
 */
export const SHIPMENT_KANBAN_COLUMNS = [
    { id: 'pending', label: 'Pending Pickup', color: '#9e9e9e' },
    { id: 'in_transit', label: 'In Transit', color: '#2196f3' },
    { id: 'out_for_delivery', label: 'Out for Delivery', color: '#ff9800' },
    { id: 'delivered', label: 'Delivered', color: '#4caf50' },
    { id: 'failed', label: 'Failed Delivery', color: '#f44336' }
];

export { KanbanView, KanbanColumn, KanbanCard, COLUMN_COLORS };
export default React.memo(KanbanView);
